import React, { FC, RefObject, useCallback, useEffect, useRef, useState } from "react";
import { createContext } from "use-context-selector";
import { LocalTrack, RemoteParticipant, Room, VideoTrack } from "twilio-video";
import { ParticipantsContextProvider } from "./ParticipantsContext";
import { patchRecord } from "../api/rooms/handleRecord";
import useQuery from "../utils/useQuery";

export type FocusedType = "local" | "remote" | "screen";

export type PermissionType = "professional" | "user" | "patient";

export type VideoChatContextType = {
  permission: PermissionType;
  handleSetPermission: (value: PermissionType) => void;
  room: Room | undefined;
  localTracks: LocalTrack[];
  remoteParticipant: RemoteParticipant | undefined;
  currentFocused: FocusedType | "";
  focusedParticipantRef: RefObject<HTMLVideoElement>;
  focusedVideoOff: boolean;
  handleFocusedVideoOff: (value: boolean) => void;
  handleSetRoom: (newRoom: Room | undefined) => void;
  handleLeaveRoom: () => void;
  handleSetRemoteParticipant: (participant: RemoteParticipant | undefined) => void;
  handleChangeFocused: (
    value: FocusedType,
    videoTrack: VideoTrack | null,
    changeCam?: boolean
  ) => void;
  connected: boolean;
  handleSetConnected: (value: boolean) => void;
  isRecording: boolean;
  setLocalTracks: (tracks: LocalTrack[]) => void;
  handleRecording: (value: boolean) => void;
};

export const VideoChatContext = createContext({} as VideoChatContextType);

export const VideoChatContextProvider = ({ children }: { children: any }) => {
  const [permission, setPermission] = useState<PermissionType>("user");
  const [room, setRoom] = useState<Room>();
  const [connected, setConnected] = useState<boolean>(false);
  const [remoteParticipant, setRemoteParticipant] = useState<RemoteParticipant>();

  const [localTracks, setLocalTracks] = React.useState<LocalTrack[]>([]);

  const [currentFocused, setCurrentFocused] = useState<FocusedType | "">("");
  const [focusedVideoOff, setFocusedVideoOff] = useState<boolean>(false);
  const focusedParticipantRef = useRef<HTMLVideoElement>(null);
  const [currentAttached, setCurrentAttached] = useState<VideoTrack>();

  const [isRecording, setIsRecording] = useState<boolean>(false);

  const handleRecording = useCallback((value: boolean) => {
    setIsRecording(value);
  }, []);

  const handleSetPermission = useCallback((value: PermissionType) => {
    setPermission(value);
  }, []);

  const handleSetRoom = useCallback((newRoom: Room | undefined) => {
    handleRecording(newRoom?.isRecording || false);
    setRoom(newRoom);
  }, []);

  const handleSetConnected = useCallback((value: boolean) => {
    setConnected(value);
  }, []);

  const handleStopRecording = useCallback(() => {
    patchRecord(
      useQuery().get("token"), {
        action: "stop",
      }
    );
  }, []);

  // Função chamada quando o usuário clicar para sair da sala
  const handleLeaveRoom = useCallback(() => {
    if (room) {
      room.localParticipant.tracks.forEach((trackPub) => {
        if (trackPub.track.kind !== "data") {
          trackPub.track.stop();
          const attachedElements = trackPub.track.detach();
          attachedElements.forEach((element) => element.remove());
        }
      });

      if (permission === "professional" && room?.isRecording) {
        handleStopRecording();
      }

      handleRecording(false);
      setRemoteParticipant(undefined);
      setCurrentFocused("");
      room.disconnect();
      setRoom(undefined);
    }
  }, [room, permission]);

  // Caso a página for recarregada envia request para parar de gravar
  useEffect(() => {
    return () => {
      if (permission === "professional" && room?.isRecording) {
        handleStopRecording();
      }
    };
  }, []);

  const handleSetRemoteParticipant = useCallback((participant: RemoteParticipant | undefined) => {
    setRemoteParticipant(participant);
  }, []);

  // Alterna o video focado
  const handleChangeFocused = useCallback(
    (value: FocusedType, videoTrack: VideoTrack | null) => {
      setCurrentFocused((prevFocused) => {
        if (prevFocused === value) return prevFocused;

        if (focusedParticipantRef.current && videoTrack) {
          videoTrack.attach(focusedParticipantRef.current);
          if (focusedParticipantRef.current.paused) {
            focusedParticipantRef.current.play();
          }
          setCurrentAttached(videoTrack);
          if (value === "screen") setFocusedVideoOff(false);
          else setFocusedVideoOff(!videoTrack.isEnabled);
          
          return value;
        }
        
        return prevFocused;
      });
  }, []);

  const handleFocusedVideoOff = useCallback((value: boolean) => {
    setFocusedVideoOff(value);
  }, []);

  // Quando trocar o video focado, remover o anterior
  useEffect(() => {
    return () => {
      if (focusedParticipantRef && focusedParticipantRef.current) {
        if (currentAttached) {
          currentAttached.detach(focusedParticipantRef.current);
        }
      }
    };
  }, [currentAttached]);

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <VideoChatContext.Provider
      value={{
        permission,
        handleSetPermission,
        room,
        remoteParticipant,
        currentFocused,
        focusedParticipantRef,
        handleSetRoom,
        handleLeaveRoom,
        handleSetRemoteParticipant,
        handleChangeFocused,
        connected,
        handleSetConnected,
        focusedVideoOff,
        handleFocusedVideoOff,
        isRecording,
        handleRecording,
        localTracks,
        setLocalTracks,
      }}
    >
      <ParticipantsContextProvider>{children}</ParticipantsContextProvider>
    </VideoChatContext.Provider>
  );
};
